// apps/web/src/pages/vendas/detalhe/historico-aba.tsx — Fase 12, Plano 31 (PV-07, T-12-50; UI-SPEC § Histórico).
// Aba "Histórico": eventos pv.* do PV (criação, anexos, vínculos, recebimentos, ajustes, cancelamentos) do mais recente
// para o mais antigo, 50 por página, com rótulo, autor, data dd/MM/yyyy HH:mm e o motivo em line-clamp-2 + "Ver mais".
// O conteúdo da aba só é montado ao abrir: a lista é buscada nesse momento (e a cada troca de página).
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import type { HistoricoPv } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { formatarEventoHistorico, totalPaginasHistorico, type EventoHistoricoApresentado } from '../../../lib/vendas/historico';

interface HistoricoAbaProps {
  readonly pedidoId: string;
  /** Muda quando o PV é recarregado (ex.: depois de um ajuste) para reler os eventos. */
  readonly versaoPv: number;
}

function EventoLinha({ evento }: { evento: EventoHistoricoApresentado }) {
  const [expandido, setExpandido] = useState(false);
  return (
    <li className="flex flex-col gap-1 border-t px-4 py-3 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-semibold">{evento.rotulo}</span>
        <span className="text-sm text-muted-foreground">{evento.autor}</span>
        <span className="ml-auto text-sm tabular-nums text-muted-foreground">{evento.dataHora}</span>
      </div>
      {evento.mudancas.length > 0 && (
        <ul className="flex flex-col text-sm text-muted-foreground">
          {evento.mudancas.map((linha) => (
            <li key={linha}>{linha}</li>
          ))}
        </ul>
      )}
      {evento.justificativa && (
        <div className="text-sm">
          <p className={`whitespace-pre-wrap break-words ${expandido ? '' : 'line-clamp-2'}`}>
            <span className="font-medium">Motivo: </span>
            {evento.justificativa}
          </p>
          {evento.verMais && (
            <button
              type="button"
              onClick={() => setExpandido((atual) => !atual)}
              aria-expanded={expandido}
              className="mt-0.5 min-h-8 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              {expandido ? 'Ver menos' : 'Ver mais'}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export function HistoricoAba({ pedidoId, versaoPv }: HistoricoAbaProps) {
  const [pagina, setPagina] = useState(1);
  const [historico, setHistorico] = useState<HistoricoPv | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(
    async (numeroPagina: number, sinal: { vivo: boolean }) => {
      setErro(false);
      setCarregando(true);
      try {
        const res = await fetch(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/historico?pagina=${numeroPagina}`, { credentials: 'include' });
        if (!sinal.vivo) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const dados = (await res.json()) as HistoricoPv;
        if (sinal.vivo) setHistorico(dados);
      } catch {
        if (sinal.vivo) setErro(true);
      } finally {
        if (sinal.vivo) setCarregando(false);
      }
    },
    [pedidoId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(pagina, sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar, pagina, versaoPv]);

  if (erro) {
    return (
      <Card>
        <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-rose-700">Não foi possível carregar o histórico. Verifique a conexão e tente novamente.</p>
          <Button type="button" variant="outline" onClick={() => void carregar(pagina, { vivo: true })}>
            <RefreshCw aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      </Card>
    );
  }

  if (historico === null) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando histórico">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (historico.itens.length === 0 && pagina === 1) {
    return (
      <Card>
        <p className="px-6 py-10 text-center text-sm font-medium">Nenhum evento registrado ainda.</p>
      </Card>
    );
  }

  const totalPaginas = totalPaginasHistorico(historico.total, historico.tamanhoPagina);

  return (
    <Card className="overflow-hidden py-0 gap-0" aria-busy={carregando}>
      <ol aria-label="Eventos do pedido de venda">
        {historico.itens.map((evento) => (
          <EventoLinha key={evento.id} evento={formatarEventoHistorico(evento)} />
        ))}
      </ol>
      {totalPaginas > 1 && (
        <nav aria-label="Paginação do histórico" className="flex items-center justify-between gap-3 border-t px-4 py-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPagina((atual) => Math.max(1, atual - 1))} disabled={pagina <= 1 || carregando}>
            <ChevronLeft aria-hidden="true" />
            Anterior
          </Button>
          <span className="inline-flex items-center gap-2 text-sm tabular-nums text-muted-foreground">
            {carregando && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Página {historico.pagina} de {totalPaginas}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))} disabled={pagina >= totalPaginas || carregando}>
            Próxima
            <ChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </Card>
  );
}
